import { Icon } from './Icon'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  disabled?: boolean
  'aria-label'?: string
  /** Width/margin for the control as a whole — applied to the wrapper, since the wrapper is what has the size. */
  className?: string
}

/**
 * A select that renders as OUR control rather than the operating system's.
 *
 * WHY `appearance-none`, WHICH IS THE WHOLE POINT OF THIS FILE.
 *
 * This used to be a bare native `<select>` with `h-[34px]` on it, and its
 * text came out cut off halfway down — Andrew reported it on the Appearance
 * field. A native select carries chrome the page does not control: its own
 * intrinsic height, its own internal padding, its own line box. Forcing a
 * height smaller than the one the OS wants does not shrink that content, it
 * CLIPS it. The text input beside it survives the identical `h-[34px]`
 * precisely because an `<input>` has no native chrome to fight.
 *
 * So the height is no longer forced at all. `appearance-none` takes the OS
 * rendering away, and the box is then built from the inside out —
 * 18px line + 7px padding top and bottom + 1px border each side = 34px,
 * matching the text input exactly, but as a RESULT of the content rather
 * than a constraint imposed on it. A control sized by what is in it cannot
 * clip what is in it.
 *
 * The chevron is ours now too, which is what `appearance-none` costs: the
 * native arrow disappears with the native everything-else. That is also what
 * finally makes README §7b's "a lock icon in place of the chevron when
 * refused" possible — UsersSection carries a comment saying it could not be
 * done while the arrow was the OS's to draw.
 */
export function Select({ value, onChange, options, disabled, className = '', ...rest }: SelectProps) {
  return (
    <div className={['relative inline-flex items-center', className].filter(Boolean).join(' ')}>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={[
          'w-full appearance-none rounded-[8px] border border-subtle bg-card',
          // The arithmetic above: 18 + 7 + 7 + 2 = 34px, without ever naming 34.
          'py-[7px] pl-2.5 pr-8 text-[13px] leading-[18px]',
          'text-hi disabled:opacity-50',
        ].join(' ')}
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {/*
        Always the chevron, deliberately. README §7b wants a lock here when a
        change is refused, and `appearance-none` is what finally makes that
        possible (see UsersSection's comment saying it could not be done while
        the arrow belonged to the OS) — but that is a visual change on its
        own, and UsersSection already prints a lock beside the reason
        underneath, so doing it here in the same breath would put two locks in
        one row. Worth doing as its own change, with someone looking at it.
      */}
      <Icon name="chevron-down" size={14} className="pointer-events-none absolute right-2.5 text-lo" />
    </div>
  )
}
